import styles from './contact-us.module.scss';
import { Component, createRef } from 'react';

import { FormControl, FormGroup } from '../../models/forms';
import { Logger } from '../../models/logger';
import { ApiConfig } from '../../services/api-config';
import { EnvironmentService } from '../../services/environment.service';
import { FileManagerService } from '../../services/file-manager.service';
import { HelperService } from '../../services/helper.service';
import { HttpProgressEvent, LightHttp } from '../../services/light-http.service';
import { UIContext } from '../../services/ui-context';
import { IOC_TYPES } from '../../context/ioc.types';
import { lazyInject } from '../../context/module';
import FormInputComponent from '../ui/forms/form-input/form-input.component';
import ReCaptcha from '../ui/recaptcha/recaptcha.component';

interface ContactUsState {
    uploaded: number;
}
interface ContactUsProps {
    embedded?: boolean;
}

export default class ContactUs extends Component<ContactUsProps, ContactUsState> {
    logger = Logger.getLogger('ContactUs');

    fileRef = createRef<HTMLInputElement>();
    @lazyInject(EnvironmentService) env: EnvironmentService;
    @lazyInject(LightHttp) http: LightHttp;
    @lazyInject(ApiConfig) apiConfig: ApiConfig;
    @lazyInject(UIContext) uiContext: UIContext;
    @lazyInject(FileManagerService) fileManagerService: FileManagerService;
    @lazyInject(HelperService) helperService: HelperService;

    private emailSentSuccess = false;
    private emailSentFailed = false;
    private token = null;

    formGroup = new FormGroup({
        email: new FormControl(undefined),
        message: new FormControl(undefined),
        token: new FormControl(undefined),
    });

    readonly state: Readonly<ContactUsState> = {
        uploaded: 0,
    };
    constructor(props) {
        super(props);
    }

    handleCorrectCaptcha(event) {
        this.token = event;
        this.formGroup.setValue({ 'token': event });
    }

    onSubmit(e) {
        if (this.formGroup.valid) {
            const formData = new FormData();
            formData.append('email', this.formGroup.controls.email.value);
            let message = this.formGroup.value.message;
            message += '  File uploads: ' + this.fileManagerService.stringifyAllUploadedFileUploads();
            formData.append('message', message);
            if (this.fileRef.current.files) {
                for (const file of Array.from(this.fileRef.current.files)) {
                    formData.append('file', file);
                }
            }
            formData.append('token', this.formGroup.value.token);
            const { response$, events$ } = this.http.request<{ error: boolean, msg: any }>(
                this.apiConfig.getSupportEmailEndpoint(),
                'POST',
                formData,
                {
                    headers: this.helperService.getCommonHttpHeaders(),
                },
            );
            response$.subscribe({
                next: (result) => {
                    if (!result.error) {
                        this.emailSentSuccess = true;
                        this.emailSentFailed = false;
                        this.uiContext.successToast('Success', `Email sent to support@${this.env.domainname}.com`);
                    } else {
                        this.emailSentFailed = true;
                        this.uiContext.errorToast('Error', 'Could not send your E-mail.' + result.msg, false, false);
                    }
                    this.forceUpdate();
                }
            })
            events$.subscribe({
                next: (result: HttpProgressEvent) => {
                    if (result.type === 'upload_progress') {
                        this.setState({
                            uploaded: parseFloat(parseFloat((100 * result.event.loaded / result.event.total) + '').toFixed(2)),
                        });
                    }
                },
                error: (err) => {
                    this.logger.error({
                        message: 'Invalid E-mail form data.',
                        error: err,
                    });
                    this.uiContext.errorToast('Error', `Failed to send E-mail. Contact us at support@${this.env.domainname}.com`, false, false);
                }
            });
        } else {
            this.emailSentFailed = true;

            this.uiContext.errorToast('Error', 'Invalid E-mail form data.', false, true);
        }
    }

    render() {
        if (this.props.embedded) return this.renderForm();
        return (<div className={styles.wrapper}>
            <div className={styles['contact-card']}>
                <h2>Contact Us</h2>
                {this.renderForm()}
            </div>
        </div>)
    }

    renderForm() {
        return (<>
                <form className="contact-us-email-form">
                    <div className={styles['form-group']}>
                        <label htmlFor="exampleFormControlInput1">Email</label>
                        <FormInputComponent control={this.formGroup.get('email')} type="email" className="form-control" placeholder="name@example.com"></FormInputComponent>
                        <div className={styles['helper-text']}>
                            Email is optional, but required if you'd like a response.
                        </div>
                    </div>
                    <div className={styles['form-group']}>
                        <label htmlFor="exampleFormControlTextarea1">Message*</label>
                        <textarea className={styles['form-control']} rows={4} required onInput={(e: any) => this.formGroup.setValue({ 'message': e.target.value })}></textarea>
                    </div>
                    <div className={styles['form-group']}>
                        <label htmlFor="exampleFormControlFile1">Attachment (Optional)</label>
                        <input ref={this.fileRef} type="file" className={styles['form-control-file']} id="exampleFormControlFile1" />
                        <div className={styles['helper-text']}>
                            Attaching the relevant file helps us diagnose technical issues faster.
                        </div>
                    </div>
                    <div className={styles['form-group']}>
                        <ReCaptcha onChange={e => this.handleCorrectCaptcha(e)}></ReCaptcha>
                    </div>

                    <button type="button" className={styles['submit-btn']} onClick={(e) => this.onSubmit(e)}>
                        Send Message
                    </button>

                    {this.state.uploaded != 0 && <span className={styles['upload-progress']}>Uploading: {this.state.uploaded}%</span>}

                </form>

                {
                    this.emailSentSuccess && <div className={`${styles.alert} ${styles.success}`}>Message sent successfully.</div>
                }
                {
                    this.emailSentFailed && <div className={`${styles.alert} ${styles.error}`}>Failed to send message. Please try again.</div>
                }

                <div className={styles['contact-info']}>
                    Or email us at support@{this.env.domainname}
                </div>
        </>)
    }
}